import React, { useMemo, useState } from 'react';
import { Checkbox } from '../../../components/Checkbox';
import Selector from '../../../components/Selector';
import useDebounce from '../../../hooks/use-debounce';
import useRole from '../../../hooks/use-role';
import { cn } from '../../../utils/cn';
import { useOrganizationsDictStore } from '../../organizations/state/use-organizations-dict-store';
import { STATUS_OPTIONS } from '../constants';
import { useSupportStore } from '../state/use-support-store';

const ASSIGNEE_TABS = [
  { value: 'all', label: 'Все' },
  { value: 'mine', label: 'Мои' },
];

const STATUS_SELECTOR_OPTIONS = STATUS_OPTIONS.filter((option) => option.value);

const ThreadFilters = () => {
  const { isMeridianOrg, isEngineer } = useRole();
  const { filters, setFilters, engineers } = useSupportStore();
  const { organizationsById } = useOrganizationsDictStore();
  const [query, setQuery] = useState(filters.q || '');

  const applySearch = useDebounce((value) => {
    setFilters({ q: value.trim() });
  }, 400);

  const organizationOptions = useMemo(
    () =>
      Object.entries(organizationsById)
        .map(([id, name]) => ({ value: String(id), label: name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    [organizationsById]
  );

  const assigneeOptions = useMemo(
    () =>
      engineers.map((item) => ({
        value: String(item.engineer_id ?? item.id),
        label: item.fio || item.username,
      })),
    [engineers]
  );

  const selects = [
    {
      key: 'status',
      placeholder: 'Все статусы',
      defaultValue: filters.status,
      onChange: (value) => setFilters({ status: value }),
      options: STATUS_SELECTOR_OPTIONS,
      show: true,
    },
    {
      key: 'organization',
      placeholder: 'Все компании',
      defaultValue: filters.organizationId,
      onChange: (value) => setFilters({ organizationId: value }),
      options: organizationOptions,
      show: isMeridianOrg,
    },
    {
      key: 'assignee',
      placeholder: 'Все исполнители',
      defaultValue: filters.assignee === 'all' ? '' : filters.assignee,
      onChange: (value) => setFilters({ assignee: value || 'all' }),
      options: [{ value: 'mine', label: 'Мои' }, ...assigneeOptions],
      show: !isEngineer,
    },
  ].filter((item) => item.show);

  return (
    <div className="flex flex-col gap-2 border-b border-gray-200 bg-white p-3">
      <h2 className="text-xl font-medium text-gray-700">Поддержка</h2>

      <input
        value={query}
        onChange={(event) => {
          const value = event.target.value;
          setQuery(value);
          applySearch(value);
        }}
        placeholder="ФИО или телефон"
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none"
      />

      {selects.map((select) => (
        <Selector
          key={select.key}
          placeholder={select.placeholder}
          defaultValue={select.defaultValue}
          onChange={select.onChange}
          options={select.options}
          className="w-full shadow-none"
        />
      ))}

      {isEngineer ? (
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1">
          {ASSIGNEE_TABS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilters({ assignee: option.value })}
              className={cn('rounded-md py-1.5 text-sm', filters.assignee === option.value ? 'bg-white font-medium text-gray-900 shadow-sm' : 'text-gray-500')}>
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      <Checkbox checked={filters.unreadOnly} label="Только непрочитанные" onChange={(event) => setFilters({ unreadOnly: event.target.checked })} />
    </div>
  );
};

export default ThreadFilters;
